import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search, Check } from 'lucide-react';
import { fmtNumber } from './format';

/**
 * MultiSelect — a searchable, checkbox dropdown.
 *
 * Built rather than using a <select multiple> because several of these lists
 * are long (538 employees, up to 2,000 merchants) and need type-ahead plus a
 * visible count of what is selected.
 */
export function MultiSelect({ label, options, value, onChange, placeholder = 'All', width = 190 }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    const list = t ? options.filter((o) => String(o.label).toLowerCase().includes(t)) : options;
    return list.slice(0, 500);
  }, [options, search]);

  const selected = new Set(value);
  const toggle = (v) => {
    const next = new Set(selected);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange([...next]);
  };

  const summary = value.length === 0
    ? placeholder
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label ?? value[0])
      : `${value.length} selected`;

  return (
    <div style={{ ...s.field, width }} ref={ref}>
      <label style={s.label}>{label}</label>
      <button type="button" style={{ ...s.control, borderColor: value.length ? '#1B2A4A' : '#E5E7EB' }}
              onClick={() => setOpen((v) => !v)}>
        <span style={{ ...s.controlText, color: value.length ? '#111827' : '#9CA3AF' }}>{summary}</span>
        {value.length > 0 ? (
          <X size={13} style={{ flexShrink: 0, color: '#6B7280' }}
             onClick={(e) => { e.stopPropagation(); onChange([]); }} />
        ) : (
          <ChevronDown size={13} style={{ flexShrink: 0, color: '#9CA3AF' }} />
        )}
      </button>

      {open && (
        <div style={s.menu}>
          <div style={s.searchRow}>
            <Search size={12} color="#9CA3AF" />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search…" style={s.searchInput} />
          </div>
          <div style={s.optionList}>
            {filtered.length === 0 && <div style={s.empty}>No matches</div>}
            {filtered.map((o) => (
              <button key={o.value} type="button" style={s.option} onClick={() => toggle(o.value)}>
                <span style={{ ...s.checkbox, ...(selected.has(o.value) ? s.checkboxOn : {}) }}>
                  {selected.has(o.value) && <Check size={10} color="#fff" strokeWidth={3} />}
                </span>
                <span style={s.optionLabel}>{o.label}</span>
                {o.count !== undefined && <span style={s.optionCount}>{fmtNumber(o.count)}</span>}
              </button>
            ))}
            {options.length > filtered.length + (search ? 0 : 0) && filtered.length === 500 && (
              <div style={s.empty}>Showing first 500 — refine your search</div>
            )}
          </div>
          {value.length > 0 && (
            <button type="button" style={s.clearAll} onClick={() => onChange([])}>
              Clear {value.length} selected
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SelectOne({ label, options, value, onChange, width = 150 }) {
  return (
    <div style={{ ...s.field, width }}>
      <label style={s.label}>{label}</label>
      <select style={{ ...s.control, ...s.nativeSelect, borderColor: value && value !== 'all' ? '#1B2A4A' : '#E5E7EB' }}
              value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, width = 190, type = 'text' }) {
  return (
    <div style={{ ...s.field, width }}>
      <label style={s.label}>{label}</label>
      <input type={type} value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             style={{ ...s.control, ...s.input, borderColor: value ? '#1B2A4A' : '#E5E7EB' }} />
    </div>
  );
}

const s = {
  field:   { display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' },
  label:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6B7280' },
  control: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',
    height: '32px', padding: '0 8px', background: '#FFFFFF',
    border: '1px solid #E5E7EB', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer', textAlign: 'left', width: '100%',
  },
  nativeSelect: { cursor: 'pointer', appearance: 'auto' },
  input:      { cursor: 'text' },
  controlText:{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menu: {
    position: 'absolute', top: '58px', left: 0, zIndex: 60,
    width: 'max(100%, 260px)', background: '#FFFFFF',
    border: '1px solid #E5E7EB', borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
  },
  searchRow:  { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', borderBottom: '1px solid #F3F4F6' },
  searchInput:{ flex: 1, border: 'none', outline: 'none', fontSize: '12px' },
  optionList: { maxHeight: '260px', overflowY: 'auto', padding: '4px' },
  option: {
    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
    padding: '6px 8px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '12px', textAlign: 'left', borderRadius: '4px',
  },
  checkbox: {
    width: '14px', height: '14px', borderRadius: '3px', border: '1.5px solid #D1D5DB',
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  checkboxOn: { background: '#1B2A4A', borderColor: '#1B2A4A' },
  optionLabel:{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' },
  optionCount:{ fontSize: '10px', color: '#9CA3AF', flexShrink: 0 },
  empty:      { padding: '10px', fontSize: '11px', color: '#9CA3AF', textAlign: 'center' },
  clearAll: {
    width: '100%', padding: '8px', background: '#F9FAFB', border: 'none',
    borderTop: '1px solid #F3F4F6', fontSize: '11px', color: '#DC2626', cursor: 'pointer',
  },
};
